import React, { useState, useEffect } from 'react';
import { X, ZoomIn, ZoomOut, RotateCcw, RotateCw } from 'lucide-react';

interface FilePreviewProps {
  url: string;
  fileName: string;
  onClose: () => void;
}

export default function FilePreview({ url, fileName, onClose }: FilePreviewProps) {
  const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);
  const isPDF = /\.pdf$/i.test(fileName);
  
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Effect to automatically open PDF in new tab
  useEffect(() => {
    if (isPDF) {
      window.open(url, '_blank');
      onClose();
    }
  }, [isPDF, url, onClose]);

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3));
    setPosition({ x: 0, y: 0 }); // Reset position on zoom
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 1));
    setPosition({ x: 0, y: 0 }); // Reset position on zoom
  };

  const handleRotateLeft = () => {
    setRotation(prev => (prev - 90) % 360);
    setPosition({ x: 0, y: 0 }); // Reset position on rotation
  };

  const handleRotateRight = () => {
    setRotation(prev => (prev + 90) % 360);
    setPosition({ x: 0, y: 0 }); // Reset position on rotation
  };

  const handleReset = () => {
    setScale(1);
    setRotation(0);
    setPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (scale > 1) {
      setIsDragging(true);
      const rect = e.currentTarget.getBoundingClientRect();
      
      // Calculate the current position in the rotated coordinate system
      const angle = (rotation * Math.PI) / 180;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      
      const currentX = position.x;
      const currentY = position.y;
      
      setDragStart({
        x: e.clientX - (currentX * cos - currentY * sin),
        y: e.clientY - (currentX * sin + currentY * cos)
      });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && scale > 1) {
      // Calculate the movement in the rotated coordinate system
      const angle = (rotation * Math.PI) / 180;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      
      // Transform the coordinates back to the image's coordinate system
      setPosition({
        x: dx * cos + dy * sin,
        y: -dx * sin + dy * cos
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // If it's a PDF, don't render the preview modal
  if (isPDF) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full h-[90vh] flex flex-col">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">{fileName}</h3>
          <div className="flex items-center gap-2">
            {isImage && (
              <>
                <button
                  onClick={handleZoomIn}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition"
                  title="Zoom In"
                >
                  <ZoomIn className="w-5 h-5" />
                </button>
                <button
                  onClick={handleZoomOut}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition"
                  title="Zoom Out"
                  disabled={scale === 1}
                >
                  <ZoomOut className="w-5 h-5" />
                </button>
                <button
                  onClick={handleRotateLeft}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition"
                  title="Rotate Left"
                >
                  <RotateCcw className="w-5 h-5" />
                </button>
                <button
                  onClick={handleRotateRight}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition"
                  title="Rotate Right"
                >
                  <RotateCw className="w-5 h-5" />
                </button>
                <button
                  onClick={handleReset}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition"
                  title="Reset"
                  disabled={scale === 1 && rotation === 0}
                >
                  <RotateCcw className="w-5 h-5" />
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        <div 
          className="flex-1 overflow-hidden relative"
          onMouseDown={isImage ? handleMouseDown : undefined}
          onMouseMove={isImage ? handleMouseMove : undefined}
          onMouseUp={isImage ? handleMouseUp : undefined}
          onMouseLeave={isImage ? handleMouseUp : undefined}
          style={{ cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
        >
          {isImage && (
            <div className="w-full h-full flex items-center justify-center">
              <img
                src={url}
                alt={fileName}
                className="max-w-full max-h-full object-contain transition-transform duration-200"
                style={{
                  transform: `rotate(${rotation}deg) scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
                  transformOrigin: 'center center',
                  userSelect: 'none'
                }}
                draggable={false}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
